import React, { useEffect, useState, useCallback } from 'react';
import {
    Input, Button, Spin, Empty, message, Popconfirm, Tag, Space,
    Pagination, Modal, Upload, Alert, Table, Typography, Tabs,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
    BellOutlined, UploadOutlined, DownloadOutlined, InboxOutlined,
    FileExcelOutlined, CloseCircleOutlined, PushpinOutlined,
} from '@ant-design/icons';
import { faqApi } from '../../services/api';
import { FaqItem } from '../../services/types';
import FaqFormModal from '../../components/admin/FaqFormModal';
import { useAuthStore } from '../../stores/authStore';
import PinnedFaqManagement from '../../components/admin/PinnedFaqManagement';

const { Dragger } = Upload;
const { Text } = Typography;

const PAGE_SIZE = 10;

const FaqPage: React.FC = () => {
    const { user } = useAuthStore();
    const [faqs, setFaqs] = useState<FaqItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('list');

    // 编辑弹窗
    const [modalOpen, setModalOpen] = useState(false);
    const [editingFaq, setEditingFaq] = useState<FaqItem | undefined>();

    // 批量导入弹窗
    const [importOpen, setImportOpen] = useState(false);
    const [importing, setImporting] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [importResult, setImportResult] = useState<{
        total: number; success: number; failed: number;
        failed_rows: { row: number; question: string; errors: string[] }[];
    } | null>(null);

    const fetchFaqs = useCallback(async (k: string = '', page = 1) => {
        setLoading(true);
        try {
            const skip = (page - 1) * PAGE_SIZE;
            const res = await faqApi.list({ keyword: k || undefined, skip, limit: PAGE_SIZE });
            if (res.success && res.data) {
                setFaqs(res.data.items || []);
                setTotal(res.data.total || 0);
            }
        } catch {
            message.error('加载 FAQ 列表失败');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFaqs();
    }, [fetchFaqs]);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchFaqs(keyword, 1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchFaqs(keyword, page);
    };

    const handleCreate = () => { setEditingFaq(undefined); setModalOpen(true); };
    const handleEdit = (item: FaqItem) => { setEditingFaq(item); setModalOpen(true); };

    const handleDelete = async (id: number) => {
        try {
            const res = await faqApi.delete(id);
            if (res.success) { message.success('删除成功'); fetchFaqs(keyword, currentPage); }
        } catch { message.error('删除失败'); }
    };

    const handleModalSuccess = () => {
        setModalOpen(false);
        fetchFaqs(keyword, currentPage);
    };

    // 下载模板
    const handleDownloadTemplate = async () => {
        try {
            const blob = await faqApi.downloadTemplate();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'faq_import_template.xlsx';
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            message.error('下载模板失败');
        }
    };

    // 打开导入弹窗
    const openImportModal = () => {
        setSelectedFile(null);
        setImportResult(null);
        setImportOpen(true);
    };

    // 确认上传
    const handleConfirmImport = async () => {
        if (!selectedFile) { message.warning('请先选择文件'); return; }
        setImporting(true);
        setImportResult(null);
        try {
            const res = await faqApi.importExcel(selectedFile);
            if (res.success && res.data) {
                setImportResult(res.data);
                message.success(res.message || '导入完成');
                fetchFaqs(keyword, 1);
                setCurrentPage(1);
            } else {
                message.error(res.message || '导入失败');
            }
        } catch (err: any) {
            message.error(err?.response?.data?.detail || '导入失败');
        } finally {
            setImporting(false);
        }
    };

    const statusTag = (active: boolean) =>
        active
            ? <Tag color="success" style={{ marginLeft: 6 }}>启用</Tag>
            : <Tag color="error" style={{ marginLeft: 6 }}>停用</Tag>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 顶部栏 */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0 32px', height: 64, background: '#fff',
                borderBottom: '1px solid #e4e4e7', flexShrink: 0,
            }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#18181b' }}>FAQ 管理</h2>
                <Space>
                    <BellOutlined style={{ fontSize: 18, color: '#71717a', cursor: 'pointer' }} />
                    <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: '#3b82f6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 600, fontSize: 14,
                    }}>
                        {user?.username?.[0]?.toUpperCase() || 'A'}
                    </div>
                </Space>
            </div>

            {/* 内容区 */}
            <div style={{ flex: 1, padding: 24, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'list',
                            label: 'FAQ 列表',
                            children: (
                                <>
                                    {/* 操作栏 */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <Space>
                                            <Input
                                                placeholder="搜索 FAQ..."
                                                prefix={<SearchOutlined style={{ color: '#a1a1aa' }} />}
                                                value={keyword}
                                                onChange={(e) => {
                                                    setKeyword(e.target.value);
                                                    if (!e.target.value) { setCurrentPage(1); fetchFaqs('', 1); }
                                                }}
                                                onPressEnter={handleSearch}
                                                style={{ width: 280 }}
                                                allowClear
                                            />
                                        </Space>
                                        <Space>
                                            <Button icon={<UploadOutlined />} onClick={openImportModal}>
                                                批量导入
                                            </Button>
                                            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                                                新增 FAQ
                                            </Button>
                                        </Space>
                                    </div>

                                    {/* FAQ 卡片列表 */}
                                    <Spin spinning={loading} style={{ flex: 1 }}>
                                        {faqs.length === 0 ? (
                                            <Empty description="暂无 FAQ 数据" style={{ marginTop: 64 }} />
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                {faqs.map((faq) => (
                                                    <div key={faq.id} style={{
                                                        background: '#fff', borderRadius: 12,
                                                        padding: '16px 20px', border: '1px solid #e4e4e7',
                                                        display: 'flex', flexDirection: 'column', gap: 10,
                                                    }}>
                                                        {/* Header */}
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                            <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 0, alignItems: 'center' }}>
                                                                <span style={{ color: '#2563eb', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>Q</span>
                                                                <span style={{ color: '#18181b', fontWeight: 500, fontSize: 14 }}>{faq.question}</span>
                                                                {statusTag(faq.is_active)}
                                                            </div>
                                                            <Space size={4}>
                                                                <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(faq)} title="编辑" />
                                                                <Popconfirm title="确定删除此 FAQ？" onConfirm={() => handleDelete(faq.id)} okText="删除" cancelText="取消">
                                                                    <Button type="text" size="small" danger icon={<DeleteOutlined />} title="删除" />
                                                                </Popconfirm>
                                                            </Space>
                                                        </div>

                                                        {/* Answer */}
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>A</span>
                                                            <div style={{ color: '#71717a', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{faq.answer}</div>
                                                        </div>

                                                        {/* Tags & 分类 */}
                                                        {(faq.category || (faq.tags_list && faq.tags_list.length > 0)) && (
                                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                                {faq.category && <Tag color="blue">{faq.category}</Tag>}
                                                                {faq.tags_list?.map((tag: string, idx: number) => (
                                                                    <Tag key={idx} color="default">{tag}</Tag>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Spin>

                                    {/* 分页 */}
                                    {total > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                                            <Pagination
                                                current={currentPage}
                                                pageSize={PAGE_SIZE}
                                                total={total}
                                                showTotal={(t) => `共 ${t} 条`}
                                                showSizeChanger={false}
                                                onChange={handlePageChange}
                                            />
                                        </div>
                                    )}
                                </>
                            ),
                        },
                        {
                            key: 'pinned',
                            label: (
                                <span>
                                    <PushpinOutlined style={{ marginRight: 6 }} />
                                    置顶管理
                                </span>
                            ),
                            children: <PinnedFaqManagement onChange={() => fetchFaqs(keyword, currentPage)} />,
                        },
                    ]}
                />
            </div>

            {/* 编辑弹窗 */}
            <FaqFormModal
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onSuccess={handleModalSuccess}
                editData={editingFaq}
            />

            {/* 批量导入弹窗 */}
            <Modal
                title="批量导入 FAQ"
                open={importOpen}
                onCancel={() => setImportOpen(false)}
                footer={null}
                width={640}
            >
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    {/* 说明 + 下载模板 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text type="secondary">请先下载模板，按格式填写后上传：</Text>
                        <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadTemplate}
                            style={{ padding: 0 }}
                        >
                            下载导入模板
                        </Button>
                    </div>

                    {/* 文件选择区域 */}
                    {!selectedFile ? (
                        <Dragger
                            accept=".xlsx,.xls"
                            showUploadList={false}
                            beforeUpload={(file) => {
                                const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
                                if (!isExcel) { message.error('仅支持 .xlsx 或 .xls 格式'); return Upload.LIST_IGNORE; }
                                if (file.size > 5 * 1024 * 1024) { message.error('文件不能超过 5MB'); return Upload.LIST_IGNORE; }
                                setSelectedFile(file);
                                return false;
                            }}
                            disabled={importing}
                            style={{ padding: '8px 0' }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ fontSize: 40, color: '#3b82f6' }} />
                            </p>
                            <p style={{ fontSize: 14, color: '#374151' }}>点击或拖拽 Excel 文件到此处</p>
                            <p style={{ fontSize: 12, color: '#9ca3af' }}>仅支持 .xlsx / .xls，文件大小不超过 5MB</p>
                        </Dragger>
                    ) : (
                        <div style={{
                            border: '1px dashed #3b82f6', borderRadius: 8, padding: '16px 20px',
                            background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Space>
                                <FileExcelOutlined style={{ fontSize: 24, color: '#16a34a' }} />
                                <div>
                                    <div style={{ fontWeight: 500, color: '#18181b' }}>{selectedFile.name}</div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {(selectedFile.size / 1024).toFixed(1)} KB
                                    </Text>
                                </div>
                            </Space>
                            <Button
                                type="text"
                                icon={<CloseCircleOutlined />}
                                onClick={() => setSelectedFile(null)}
                                disabled={importing}
                                style={{ color: '#71717a' }}
                            />
                        </div>
                    )}

                    {/* 确认上传按钮 */}
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={handleConfirmImport}
                        loading={importing}
                        disabled={!selectedFile}
                        block
                        size="large"
                    >
                        {importing ? '正在导入...' : '确认上传'}
                    </Button>

                    {/* 导入结果 */}
                    {importResult && (
                        <Alert
                            type={importResult.failed === 0 ? 'success' : 'warning'}
                            message={`导入完成：共 ${importResult.total} 条，成功 ${importResult.success} 条，失败 ${importResult.failed} 条`}
                            description={
                                importResult.failed_rows.length > 0 ? (
                                    <Table
                                        size="small"
                                        style={{ marginTop: 8 }}
                                        dataSource={importResult.failed_rows.map((r, i) => ({ ...r, key: i }))}
                                        columns={[
                                            { title: '行号', dataIndex: 'row', width: 60 },
                                            { title: '问题预览', dataIndex: 'question', ellipsis: true },
                                            { title: '错误原因', dataIndex: 'errors', render: (e: string[]) => e.join('；') },
                                        ]}
                                        pagination={false}
                                    />
                                ) : undefined
                            }
                        />
                    )}
                </Space>
            </Modal>
        </div>
    );
};

export default FaqPage;

